import React, { useState, useEffect, useRef } from 'react';
import { Film, Music, Play, ExternalLink, Star, Calendar } from 'lucide-react';

const CreativeProjects = ({ data }) => {
  const [isVisible, setIsVisible] = useState(true); // start visible to avoid blank-screen race on mobile direct-link landings
  const [activeProject, setActiveProject] = useState(null);
  const [creativeSparks, setCreativeSparks] = useState([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.05, rootMargin: '100px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    // Create creative sparks for cinematic effect
    const sparks = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      delay: Math.random() * 4,
      duration: Math.random() * 6 + 4,
      opacity: Math.random() * 0.4 + 0.1,
      type: i % 4 === 0 ? 'spark' : 'bubble'
    }));
    setCreativeSparks(sparks);

    return () => observer.disconnect();
  }, []);

  const getProjectIcon = (type) => {
    if (type.includes('Musical')) return Music;
    if (type.includes('Film')) return Film;
    if (type.includes('Series')) return Play;
    return Star;
  };

  const getProjectGradient = (index) => {
    const gradients = [
      'from-purple-500/20 to-pink-500/20 border-purple-400/50',
      'from-amber-500/20 to-orange-500/20 border-amber-400/50',
      'from-blue-500/20 to-cyan-500/20 border-blue-400/50'
    ];
    return gradients[index % gradients.length];
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'field development':
        return 'text-purple-300 border-purple-400/50 bg-purple-900/20';
      case 'living transmission':
        return 'text-teal-300 border-teal-400/50 bg-teal-900/20';
      case 'evolutionary completion':
        return 'text-slate-300 border-slate-400/50 bg-slate-900/20';
      default:
        return 'text-blue-300 border-blue-400/50 bg-blue-900/20';
    }
  };

  return (
    <section 
      id="projects" 
      ref={sectionRef}
      className="relative py-24 bg-gradient-to-b from-blue-900/20 via-slate-900 to-purple-900/30"
    >
      {/* Creative Field Background */}
      <div className="absolute inset-0 overflow-hidden opacity-20">
        <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-purple-400/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-amber-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-2/3 left-2/3 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>

      {/* Floating Creative Sparks */}
      <div className="absolute inset-0 pointer-events-none">
        {creativeSparks.map((spark) => (
          <div
            key={spark.id}
            className={`absolute rounded-full animate-organic-float ${
              spark.type === 'spark' 
                ? 'bg-gradient-to-r from-amber-300/30 to-orange-300/20' 
                : 'bg-purple-300/20'
            }`}
            style={{
              left: `${spark.x}%`,
              top: `${spark.y}%`,
              width: `${spark.size}px`,
              height: `${spark.size}px`,
              animationDelay: `${spark.delay}s`,
              animationDuration: `${spark.duration}s`,
              opacity: spark.opacity
            }}
          />
        ))}
      </div>

      <div className="relative z-10 container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          {/* Creative Header */}
          <div className={`text-center mb-20 transition-all duration-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h2 className="text-5xl md:text-6xl font-extralight mb-6 tracking-wide">
              <span className="bg-gradient-to-r from-purple-200 via-amber-200 to-blue-200 bg-clip-text text-transparent">
                {data.title}
              </span>
            </h2>
            <p className="text-2xl text-purple-300 font-light mb-8">{data.subtitle}</p>
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-purple-400 to-transparent mx-auto mb-8"></div>
            <p className="text-lg text-slate-400 max-w-4xl mx-auto leading-relaxed font-light">
              Beyond books—creative expressions that catalyze consciousness evolution
            </p>
          </div>

          {/* Projects Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {data.works.map((project, index) => {
              const IconComponent = getProjectIcon(project.type);
              const gradientClass = getProjectGradient(index);
              const statusColor = getStatusColor(project.status);
              
              return (
                <div
                  key={project.id}
                  className={`group relative transition-all duration-500 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                  style={{ transitionDelay: `${index * 200}ms` }}
                  onMouseEnter={() => setActiveProject(index)}
                  onMouseLeave={() => setActiveProject(null)}
                >
                  <div className={`h-full bg-gradient-to-br ${gradientClass} backdrop-blur-sm rounded-3xl p-8 border transition-all duration-700 hover:shadow-2xl ${
                    activeProject === index 
                      ? 'border-purple-400/70 shadow-lg shadow-purple-400/20 transform scale-105' 
                      : 'border-slate-700/50 hover:border-amber-400/50 hover:shadow-amber-400/10'
                  }`}>
                    
                    {/* Project Header */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="p-3 rounded-full bg-gradient-to-r from-slate-400/25 to-purple-400/25 group-hover:from-slate-400/35 group-hover:to-purple-400/35 transition-all duration-600">
                        <IconComponent className="w-6 h-6 text-purple-300 group-hover:text-purple-200 transition-colors duration-600" />
                      </div>
                      
                      {/* Status Badge */}
                      <div className={`text-xs font-light px-3 py-1 rounded-full border ${statusColor}`}>
                        {project.status}
                      </div>
                    </div>

                    {/* Project Content */}
                    <div className="space-y-4">
                      <h3 className="text-xl font-light text-slate-200 group-hover:text-slate-100 transition-colors duration-600 leading-relaxed">
                        {project.title}
                      </h3>

                      <div className="text-sm text-purple-300/90 font-light">
                        {project.type}
                      </div>

                      <p className="text-slate-400 leading-relaxed font-light text-sm">
                        {project.description}
                      </p>
                    </div>

                    {/* Creative Flow Indicator */}
                    <div className={`absolute bottom-6 left-8 flex items-center space-x-2 transition-all duration-700 ${
                      activeProject === index ? 'opacity-100' : 'opacity-0'
                    }`}>
                      <div className="w-8 h-px bg-gradient-to-r from-purple-400 to-amber-400"></div>
                      <Star className="w-4 h-4 text-purple-400" />
                    </div>

                    {/* Creative Ripple Effect */}
                    {activeProject === index && (
                      <div className="absolute top-8 right-8">
                        <div className="w-3 h-3 rounded-full border-2 border-purple-400/50 animate-ping"></div>
                        <div className="absolute inset-0 w-3 h-3 rounded-full border border-amber-400/30 animate-ping delay-300"></div>
                      </div>
                    )}

                    {/* Creative Flow Line */}
                    <div className="absolute bottom-4 left-8 right-8 h-px bg-gradient-to-r from-transparent via-purple-400/40 to-transparent group-hover:via-purple-400/70 transition-all duration-600"></div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Creative Vision Statement */}
          <div className={`mt-24 text-center transition-all duration-500 delay-1200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <div className="max-w-4xl mx-auto">
              <div className="relative bg-gradient-to-r from-purple-900/30 to-amber-900/30 backdrop-blur-sm rounded-2xl p-8 border border-purple-400/30">
                <div className="absolute top-6 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-purple-400 rounded-full">
                  <div className="absolute inset-0 bg-purple-400 rounded-full animate-ping opacity-75"></div>
                </div>
                
                <p className="text-xl text-slate-200 leading-relaxed font-light mb-4 pt-4">
                  Each creative project serves consciousness evolution:
                </p>
                <p className="text-2xl font-light bg-gradient-to-r from-purple-300 to-amber-300 bg-clip-text text-transparent">
                  Art as vehicle for planetary transformation
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CreativeProjects;